import React, { PureComponent } from 'react'
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs'
import { List, AutoSizer, WindowScroller, CellMeasurer, CellMeasurerCache } from 'react-virtualized'
import classNames from 'classnames'
import { shallowEqual } from 'recompose'

import type { Query } from '../types'
import MapButton from './MapButton'
import CourseMapContainer from 'containers/CourseMapContainer'
import ProCard from 'modules/pros/components/ProCard'
import CourseCard from 'modules/courses/components/CourseCard'
import EventCard from 'modules/events/components/EventCard'
import ServiceCard from 'modules/events/components/ServiceCard'
import InlineLoader from 'modules/common/components/inlineloader'
import { GridContainer,  GridRow, GridCol } from 'modules/common/components/grid'
import { H2 }  from 'modules/common/components/elements'
import Pill from 'modules/common/components/pill'
import ProductFooter from './ProductFooter'

import { APP_THEME } from 'config'

const cacheCourses = new CellMeasurerCache({
  fixedWidth: true,
  defaultHeight: 300,
});

const isProActive = (props, Component) => {
  const courseIds = props.default_course ? props.default_course.map(({id}) => parseInt(id, 10)) : []
  const {
    activeMarkers = []
  } = props
  if(activeMarkers.some(id => courseIds.includes(id))){
    return <Component {...props} active={true} />
  }
  return <Component {...props} />
}
const withProActive = (Component) => (props) => isProActive(props, Component)
const Pro = withProActive(ProCard)
const Course = CourseCard

const getQueryVars = (search): object => {
  const query = search.substring(1);
  const vars = query.split("&").map(ivar => ivar.split("="));

  return Object.assign(
    {},
    ...vars.map(item => ({ [item[0]]: decodeURIComponent(item[1]) }))
  );
};


class SearchResults extends PureComponent {
  state = {
    activeCourses: [],
    activeMarkers: [],
    showMobileMap: false,
    activeTab: 0,
    tabsVisited: [],
  }

  componentWillMount() {
    this.loadData();
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.location !== prevProps.location) {
      this.loadData();
    }
    if(!shallowEqual(this.props.courses, prevProps.courses)){
      this.clearCourseScrollCache()
    }
  }

  componentDidMount() {
    if (this.courseList) {
      this.clearCourseScrollCache()
    }
  }

  componentWillReceiveProps(nextProps){
    if (this.props.location !== nextProps.location) {
      this.setState({
        activeTab: 0,
        tabsVisited: [],
      })
    }
  }

  clearCourseScrollCache = () => {
    setTimeout(() => {
      cacheCourses.clearAll();
        if (this.courseList) {
          this.courseList.recomputeRowHeights()
          this.courseList.scrollToRow(0)
        }
    }, 0);
  }

  loadData() {
    const { location, doSearch } = this.props;
    const queryFromLocation = getQueryVars(location.search);
    const {
      text
    } = queryFromLocation
    
    const query: Query = ({
      type: queryFromLocation.type,
      text: text,
    }: Query);

    doSearch(query);
  }

  
  renderEvent = (event, visible = false) => {
    const { 
      courses,
      query: {location},
      pro,
    } = this.props;
    const { 
      activeMarkers
    } = this.state
    let courseAddress = event.location
    let active = false

    if(event.course) {
      const { id } = event.course && event.course instanceof Array && event.course.length > 0 ? event.course[0] : event.course;
      if(id && courses) {
        const course = courses[id];
        if(course && course.address) {
          courseAddress = course.address;
        }
        active = activeMarkers.includes(parseInt(id, 10))
      }
    }
   
    return (
      <EventCard 
        fluid
        active={active}
        activeOnHover={true}
        onMouseOver={this.handleProductHover}
        onMouseOut={this.handleMouseOut}
        key={event.id}
        event={{...event, place: undefined}}
        courseAddress={courseAddress}
        distanceTo={location}
      >
        { 
          <ProductFooter {...pro[0]} id={event.uid} place={event.place} /> 
        }
      </EventCard>
    )
  };

  renderPackage = (event) => {
    const { 
      courses,
      query: {location},
    } = this.props;
    const { 
      activeMarkers
    } = this.state
    let courseAddress = event.location;
    let active = false

    if(event.course) {
      const { id } = event.course && event.course instanceof Array && event.course.length > 0 ? event.course[0] : event.course;
      if(id && courses) {
        const course = courses[id];
        if(course && course.address) {
          courseAddress = course.address;
        }
        active = activeMarkers.includes(parseInt(id, 10))
      }
    }
    return (
      <ServiceCard
        fluid
        active={active}
        activeOnHover={true}
        onMouseOver={this.handleProductHover}
        onMouseOut={this.handleMouseOut}
        key={event.id}
        event={{...event, place: undefined}}
        courseAddress={courseAddress}
        distanceTo={location}
      >
        <ProductFooter {...event.pro[0]} id={event.uid} place={event.place} />
      </ServiceCard>
    )
  };


  handleCourseHover = (item = {}) => {
    const {
      id
    } = item
    if(id){
      this.setState({
        activeCourses: [parseInt(id, 10)]
      })
    }
  }

  handleProHover = (item = {}) => {
    const {
      default_course = []
    } = item
    const ids = default_course.map(({id}) => parseInt(id, 10))
    if(ids.length){
      this.setState({
        activeCourses: ids
      })
    }
  }

  handleProductHover = (item = {}) => {
    const {
      course = []
    } = item
    const ids = course.map(({id}) => parseInt(id, 10))
    if(ids.length){
      this.setState({
        activeCourses: ids
      })
    }
  }

  handleMouseOut = () => {
    this.setState({
      activeCourses: [],
      activeMarkers: []
    })
  }

  setActiveMarkers = (marker) => {
    const {
      id
    } = marker
    if(id){
      this.setState({
        activeMarkers: [parseInt(id, 10)]
      })
    }
  }
  
  clearActiveMarkers = () => {
    this.setState({
      activeMarkers: []
    })
  }

  onTabSelect = (index) => {
    this.setState({
      activeTab: index,
      activeMarkers: [],
      tabsVisited: [...this.state.tabsVisited, index]
    })
  }

  rowRenderer = ({ index, isScrolling, key, parent, style, itemsPerRow, organizers }) => {
    const {
      activeMarkers
    } = this.state
    const items = [];
    const fromIndex = index * itemsPerRow;
    const toIndex = Math.min(fromIndex + itemsPerRow, organizers.length);
    for (let i = fromIndex; i < toIndex; i++) {
      items.push(
        <div
          className={`col virtual--item virtual--item-${itemsPerRow}`}
          key={organizers[i].id}
        >
          <Pro
            activeMarkers={activeMarkers}
            activeOnHover={true}
            onMouseOver={this.handleProHover}
            onMouseOut={this.handleMouseOut}
            fluid
            id={organizers[i].id}
            showArea={true}
            {...organizers[i]}
          />
        </div>
      )
    }
    return (
      <GridRow
        className='virtual--row'
        key={key}
        style={style}
        >
        {items}
      </GridRow>
      
    )
  }
  
  rowRendererCourse = ({ index, isScrolling, key, parent, style, itemsPerRow, courses }) => {
    const {
      activeMarkers
    } = this.state
    const items = [];
    const fromIndex = index * itemsPerRow;
    const toIndex = Math.min(fromIndex + itemsPerRow, courses.length);
    for (let i = fromIndex; i < toIndex; i++) {
      items.push(
        <div
          className={`col virtual--item virtual--item-${itemsPerRow}`}
          key={courses[i].id}
        >
          <Course
            fluid
            active={activeMarkers.includes(parseInt(courses[i], 10))}
            activeOnHover={true}
            onMouseOver={this.handleCourseHover}
            onMouseOut={this.handleMouseOut}
            id={courses[i].id}
            showArea={true}
            {...courses[i]}
          />
        </div>
      )
    }
    return (
      <CellMeasurer
        cache={cacheCourses}
        columnIndex={0}
        key={key}
        parent={parent}
        rowIndex={index}
      >
        <GridRow
          className='virtual--row'
          style={{...style, overflowY: 'hidden'}}
          >
          {items}
        </GridRow>
      </CellMeasurer>
    )
  }
  rowRendererPackage = ({ index, isScrolling, key, parent, style, itemsPerRow, packages }) => {
    const items = [];
    const fromIndex = index * itemsPerRow;
    const toIndex = Math.min(fromIndex + itemsPerRow, packages.length);
    for (let i = fromIndex; i < toIndex; i++) {
      items.push(
        <div
          className={`col virtual--item virtual--item-${itemsPerRow}`}
          key={packages[i].id}
        >
          { this.renderPackage(packages[i]) }
        </div>
      )
    }
    return (
      <GridRow
        className='virtual--row'
        key={key}
        style={style}
        >
        {items}
      </GridRow>
      
    )
  }
  rowRendererEvent = ({ index, isScrolling, key, parent, style, itemsPerRow, events }) => {
    const items = [];
    const fromIndex = index * itemsPerRow;
    const toIndex = Math.min(fromIndex + itemsPerRow, events.length);
    for (let i = fromIndex; i < toIndex; i++) {
      items.push(
        <div
          className={`col virtual--item virtual--item-${itemsPerRow}`}
          key={events[i].id}
        >
          { 
            this.renderEvent(events[i])
          }
        </div>
      )
    }
    return (
      <GridRow
        className='virtual--row'
        key={key}
        style={style}
        >
        {items}
      </GridRow>
      
    )
  }

  hideMobileMap = () => {
    this.setState({
      showMobileMap: false
    })
  }

  showMobileMap = () => {
    this.setState({
      showMobileMap: true
    })
  }



  renderResults = () => {
    const { 
      isPrimaryResults,
      localized,
      organizers = [],
      courses = [],
      packageCourses = [],
      packages,
      hasPackages,
      query,
      //  paginatedResults,
      // events = [],
      // hasEvents,
    } = this.props
    
    const searchTerm = query.text || query.location.label

    const {
      activeCourses,
      activeMarkers,
      showMobileMap,
      activeTab,
      tabsVisited,
    } = this.state

    const showOnlySuggestions = !isPrimaryResults && courses.length === 0 && hasPackages
    
    const showCourses = (activeTab === 2 || showOnlySuggestions) ? packageCourses : courses

    const wrapperClass = classNames({'search--suggestions': showOnlySuggestions})
    const buttonWrapClass = classNames('search-results-show-map-button hide-for-large', {'search--suggestions-map-button': showOnlySuggestions})
    
    
    if (hasPackages) {
        return (
        <Tabs
            id="results"
            onSelect={this.onTabSelect}
            selectedIndex={showOnlySuggestions ? 2 : undefined}
            className={wrapperClass}
          >
            <div className={buttonWrapClass}>
              <MapButton
                onClick={this.showMobileMap}
                text={localized('search.results.map_open_button_text')}
              />
            </div>
            <TabList className={
              [
                'react-tabs__tab-list': true,
                'react-tabs__tab-list--search': true,
                {
                  'react-tabs__tab-list--hidden': showOnlySuggestions,
                }
              ]
            }>
              <Tab
                className={
                  [
                    'react-tabs__tab': true,
                    [`react-tabs__tab--theme-${APP_THEME}`]: true,
                  ]
              }>
                {localized("search.results.tab2")}
              </Tab>
              <Tab
                className={
                  [
                    'react-tabs__tab': true,
                    [`react-tabs__tab--theme-${APP_THEME}`]: true,
                  ]
              }>
                {localized("search.results.tab1")} { (organizers.length > 0) && <Pill plainTextOnSmall visible={(activeTab !== 1 && !tabsVisited.includes(1)) ? true : undefined}>{organizers.length}</Pill> }
              </Tab>
              <Tab
                className={
                  [
                    'react-tabs__tab': true,
                    [`react-tabs__tab--theme-${APP_THEME}`]: true,
                  ]
              }>
                {localized("search.results.tab3")} { (packages.length > 0) && <Pill plainTextOnSmall visible={(activeTab !== 2 && !tabsVisited.includes(2)) ? true : undefined}>{packages.length}</Pill> }
              </Tab>
              {/* <Tab
                className={
                  [
                    'react-tabs__tab': true,
                    [`react-tabs__tab--theme-${APP_THEME}`]: true,
                  ]
              }>
                {localized("search.results.tab4")}
              </Tab> */}
            </TabList>
            <GridContainer fluid className="container--search-results">
              <GridRow className="row--search-results">
                <GridCol className="search-resuls" xs={12} lg={7}>
                  <TabPanel className="react-tabs__tab-panel--full-width react-tabs__tab-panel--search">
                  { courses && (
                    <H2 doubleBottomMargin>{`${localized("search.results.tab2")}: ${courses.length} ${courses.length === 1 ? localized("search.results.for_singular") : localized("search.results.for_plural")} "${searchTerm}"`}</H2>
                  )}
                    <WindowScroller>
                      {({height, isScrolling, registerChild, onChildScroll, scrollTop}) => (
                          <AutoSizer disableHeight>
                            {({width}) => {
                              let itemsPerRow = 3 // = Math.floor(width / 333);
                              if(width < 768){
                                itemsPerRow = 2
                              }
                              if(width < 500){
                                itemsPerRow = 1
                              }
                              const rowCount = Math.ceil(courses.length / itemsPerRow);
                              
                              return (
                                <List
                                  ref={ref => this.courseList = ref}
                                  autoHeight
                                  height={height}
                                  isScrolling={isScrolling}
                                  onScroll={onChildScroll}
                                  rowCount={rowCount}
                                  rowRenderer={({ index, isScrolling, key, style, parent }) => this.rowRendererCourse({index, isScrolling, key, style, parent, itemsPerRow, courses}) }
                                  deferredMeasurementCache={cacheCourses}
                                  rowHeight={cacheCourses.rowHeight}
                                  scrollTop={scrollTop}
                                  width={width}
                                  overscanRowCount={5}
                                />
                            )}}
                          </AutoSizer>
                      )}
                    </WindowScroller>
                  </TabPanel>
                  <TabPanel className="react-tabs__tab-panel--full-width react-tabs__tab-panel--search">
                    { (organizers) && (
                      <H2 doubleBottomMargin>{`${localized("search.results.tab1")}: ${organizers.length} ${organizers.length === 1 ? localized("search.results.for_singular") : localized("search.results.for_plural")} "${searchTerm}"`}</H2>
                    )}
                    <WindowScroller>
                      {({height, isScrolling, registerChild, onChildScroll, scrollTop}) => (
                          <AutoSizer disableHeight>
                            {({width}) => {
                              let itemsPerRow = 3 // = Math.floor(width / 333);
                              if(width < 768){
                                itemsPerRow = 2
                              }
                              if(width < 500){
                                itemsPerRow = 1
                              }
                              const rowCount = Math.ceil(organizers.length / itemsPerRow);
                              
                              return (
                                <List
                                  ref={registerChild}
                                  autoHeight
                                  height={height}
                                  isScrolling={isScrolling}
                                  onScroll={onChildScroll}
                                  rowCount={rowCount}
                                  rowRenderer={({ index, isScrolling, key, style, parent }) => this.rowRenderer({index, isScrolling, key, style, parent, itemsPerRow, organizers}) }
                                  rowHeight={300}
                                  scrollTop={scrollTop}
                                  width={width}
                                  overscanRowCount={5}
                                />
                            )}}
                          </AutoSizer>
                      )}
                    </WindowScroller>
                  </TabPanel>
                  <TabPanel className="react-tabs__tab-panel--full-width react-tabs__tab-panel--search">
                  { (isPrimaryResults) && (
                    <H2 doubleBottomMargin>
                      {`${localized("search.results.tab3")}: ${packages.length} ${packages.length === 1 ? localized("search.results.for_singular") : localized("search.results.for_plural")} "${searchTerm}"`}
                      </H2>
                  )}
                  { (!isPrimaryResults) && (
                    <H2 doubleBottomMargin>{localized("search.results.no_results")} {localized("search.results.no_results_suggestions")}</H2>
                  )}
                    { hasPackages && (
                      <WindowScroller>
                        {({height, isScrolling, registerChild, onChildScroll, scrollTop}) => (
                          <AutoSizer disableHeight>
                            {({width}) => {
                              let itemsPerRow = 3
                              if(width < 768){
                                itemsPerRow = 2
                              }
                              if(width < 500){
                                itemsPerRow = 1
                              }
                              const rowCount = Math.ceil(packages.length / itemsPerRow);
                              
                              return (
                                <List
                                  ref={registerChild}
                                  autoHeight
                                  height={height}
                                  isScrolling={isScrolling}
                                  onScroll={onChildScroll}
                                  rowCount={rowCount}
                                  rowRenderer={({ index, isScrolling, key, style, parent }) => this.rowRendererPackage({index, isScrolling, key, style, parent, itemsPerRow, packages}) }
                                  rowHeight={370}
                                  scrollTop={scrollTop}
                                  width={width}
                                  overscanRowCount={5}
                                />
                            )}}
                          </AutoSizer>
                        )}
                      </WindowScroller>)
                    }
                  </TabPanel>
                  {/* <TabPanel className="react-tabs__tab-panel--full-width react-tabs__tab-panel--search">
                      { hasEvents && (
                        <WindowScroller>
                          {({height, isScrolling, registerChild, onChildScroll, scrollTop}) => (
                            <AutoSizer disableHeight>
                              {({width}) => {
                                let itemsPerRow = 3
                                if(width < 768){
                                  itemsPerRow = 2
                                }
                                if(width < 500){
                                  itemsPerRow = 1
                                }
                                const rowCount = Math.ceil(events.length / itemsPerRow);
                                
                                return (
                                  <List
                                    ref={registerChild}
                                    autoHeight
                                    height={height}
                                    isScrolling={isScrolling}
                                    onScroll={onChildScroll}
                                    rowCount={rowCount}
                                    rowRenderer={({ index, isScrolling, key, style, parent }) => this.rowRendererEvent({index, isScrolling, key, style, parent, itemsPerRow, events}) }
                                    rowHeight={410}
                                    scrollTop={scrollTop}
                                    width={width}
                                    overscanRowCount={5}
                                  />
                              )}}
                            </AutoSizer>
                          )}
                        </WindowScroller>
                      )}
                      {
                        !hasEvents && <GridRow><GridCol xs={12}><H2 className="text--center" tripleBottomMargin>{localized("search.results.no_group_lessons")}</H2></GridCol></GridRow>
                      }
                    
                  </TabPanel> */}
                </GridCol>
                <GridCol 
                  xs={12}
                  lg={5}
                  className={`map--col ${showMobileMap ? 'map--col-visible' : ''}`}
                  noRightPadding
                >
                  <CourseMapContainer
                    rawCourses={showCourses}
                    ids={courses}
                    activeCourses={[...activeCourses, ...activeMarkers]}
                    zoom={14}
                    minZoom={2}
                    minimumClusterSize={1000}
                    onMarkerMouseOver={this.setActiveMarkers}
                    onMarkerMouseOut={this.clearActiveMarkers}
                    visible={showMobileMap}
                    disableFiClubLoading={true}
                  />
                  <div className="search-results-hide-map-button hide-for-large">
                    <MapButton
                      onClick={this.hideMobileMap}
                      text={localized('search.results.map_close_button_text')}
                    />
                  </div>
                {/*
                */}
                </GridCol>
              </GridRow>
          </GridContainer>
        </Tabs>
        )
    } else {
      return (
        <GridContainer>
          <GridRow>
            <GridCol xs={12}>
              <H2 className="text--center" tripleBottomMargin>
                {localized('search.results.no_results')}
              </H2>
            </GridCol>
          </GridRow>
        </GridContainer>
      );
    }
  };

  render() {
    const { pagination } = this.props;
    if (pagination.isFetching) {
      return <InlineLoader />;
    }
    return this.renderResults()
  }
}

export default SearchResults



// WEBPACK FOOTER //
// ./src/modules/search/components/SearchResults.js